import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '../../components/ui/dialog';
import {
  Save,
  ArrowLeft,
  ToggleLeft,
  Loader2,
  Bot,
  ExternalLink,
  Globe,
  CheckCircle,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { selectedClientSelector } from '../../store/slices/selectedClient';
import { setSelectedClient } from '../../store/slices/selectedClient';
import { showLoading, hideLoading } from '../../store/slices/loadingState';
import {
  API_NEWCLIENT,
  API_CLIENTS_NAME,
  API_KEY
} from '../../utils/constants';
import { authSelector } from '../../store/slices/auth';

const PLATFORM_LIMIT_INFO = {
  google: {
    limit: '4,000 characters',
    details:
      "This gives you plenty of room, but Google recommends keeping responses concise, professional, and directly addressing the customer's feedback."
  },
  facebook: {
    limit: '8,000 characters',
    details:
      'Facebook treats a review reply as a "comment" on the review post. The standard Facebook comment character limit is 8,000 characters.'
  },
  yelp: {
    limit: '5,000 characters',
    details:
      "Yelp allows lengthy reviews and detailed responses. Both the user's review and the business owner's public/private response share the 5,000-character maximum limit."
  },
  tripadvisor: {
    limit: 'No official strict limit',
    details:
      'TripAdvisor\'s management guidelines officially state there is no strict character limit for management responses, but they explicitly advise: "There is no character limit but remember: nobody wants to read an essay." Most third-party management tools cap this at around 5,000 characters just to be safe.'
  },
  opentable: {
    limit: 'Up to 10,000 characters',
    details:
      'OpenTable allows restaurants to reply either publicly or privately via Direct Message. The OpenTable messaging system caps messages and responses at 10,000 characters.'
  },
  bing: {
    limit: 'N/A (Read-only/Aggregated)',
    details:
      'Bing Places primarily aggregates its reviews from third-party sites like Facebook, Yelp, and TripAdvisor. Because Bing does not host a native review system, business owners cannot reply to reviews directly on Bing. You must navigate to the source platform to post a reply.'
  },
  bbb: {
    limit: 'No strict character limit published',
    details:
      'BBB handles "reviews" and "complaints" differently. Responses to complaints are sent back and forth through the BBB\'s structured dispute resolution portal. There is no stated maximum, but formatting (like URLs or HTML) is often stripped or restricted.'
  },
  angi: {
    limit: 'No official strict limit',
    details:
      'Angi does not publish a strict character limit for provider responses in their developer or business documentation. Generally, text fields on the platform allow for comprehensive, long-form explanations.'
  }
};

const ReviewsSettings = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { selectedClient } = useSelector(selectedClientSelector);
  const { userInfo } = useSelector(authSelector);

  const SEO_AUDIT_API =
    process.env.REACT_APP_SEO_AUDIT_API ||
    'https://yojoblpdy5.execute-api.us-east-1.amazonaws.com/prod';

  // Toggle states - initialized from selectedClient
  const [automaticReviews, setAutomaticReviews] = useState(false);
  const [autoPublishAbove, setAutoPublishAbove] = useState(4);
  const [requireApprovalBelow, setRequireApprovalBelow] = useState(3);
  const [singlePersonBusiness, setSinglePersonBusiness] = useState(false);
  const [noReviews, setNoReviews] = useState(false);

  // AI Settings states
  const [brandTone, setBrandTone] = useState('professional');
  const [maxAiCharacters, setMaxAiCharacters] = useState({
    google: 4000,
    yelp: 5000,
    facebook: 8000,
    tripadvisor: 5000,
    opentable: 10000,
    bbb: 1000,
    bing: 1000,
    angi: 1000
  });
  const [aiContext, setAiContext] = useState('');

  // Platform source parameters
  const [reviewPlatforms, setReviewPlatforms] = useState({
    facebook: { pageId: '', accessToken: '' }
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);

  // Load initial values from selectedClient
  useEffect(() => {
    if (selectedClient) {
      // Artificial delay to show skeleton
      setTimeout(async () => {
        setAutomaticReviews(selectedClient.automaticReviews ?? true);
        setSinglePersonBusiness(selectedClient.singlePerson ?? false);
        setNoReviews(
          selectedClient.clientFlags?.includes('NO_REVIEWS') ?? false
        );
        setBrandTone(selectedClient.brandTone ?? 'professional');

        // Handle backwards compatibility for single number vs object mapped limits
        if (selectedClient.maxAiCharacters) {
          if (typeof selectedClient.maxAiCharacters === 'number') {
            // Legacy format, apply globally
            const legacyVal = selectedClient.maxAiCharacters;
            setMaxAiCharacters({
              google: legacyVal,
              yelp: legacyVal,
              facebook: legacyVal,
              tripadvisor: legacyVal,
              opentable: legacyVal,
              bbb: legacyVal,
              bing: legacyVal,
              angi: legacyVal
            });
          } else {
            // New format, merge with defaults
            setMaxAiCharacters((prev) => ({
              ...prev,
              ...selectedClient.maxAiCharacters
            }));
          }
        }

        setAiContext(selectedClient.aiContext ?? '');

        // Load reviewPlatforms from the dedicated ReviewSettings table
        const locationId =
          selectedClient?.id || selectedClient?.currentClientId;
        const clientName =
          selectedClient?.clientName ||
          selectedClient?.mergeFields?.['~Name'] ||
          '';
        const city = selectedClient?.mergeFields?.['~City'] || '';

        try {
          const settingsRes = await axios.get(
            `${SEO_AUDIT_API}/reviews/settings`,
            { params: { locationId } }
          );
          const saved = settingsRes.data?.settings || {};
          const savedPlatforms = saved.reviewPlatforms || {};

          // Merge saved settings with smart defaults from client data
          if (saved.brandTone) setBrandTone(saved.brandTone);
          if (saved.aiContext) setAiContext(saved.aiContext);
          if (saved.maxAiCharacters) {
            setMaxAiCharacters((prev) => ({
              ...prev,
              ...saved.maxAiCharacters
            }));
          }
          if (saved.automaticReviews !== undefined) {
            setAutomaticReviews(saved.automaticReviews);
          }
          if (saved.autoPublishAbove !== undefined) {
            setAutoPublishAbove(saved.autoPublishAbove);
          }
          if (saved.requireApprovalBelow !== undefined) {
            setRequireApprovalBelow(saved.requireApprovalBelow);
          }

          setReviewPlatforms({
            ...savedPlatforms,
            facebook: {
              pageId: savedPlatforms.facebook?.pageId || '',
              accessToken: savedPlatforms.facebook?.accessToken || ''
            }
          });
        } catch (err) {
          console.warn('[ReviewsSettings] Could not load saved settings:', err);
          // Fall back to client defaults
          const savedPlatforms = selectedClient.reviewPlatforms || {};
          setReviewPlatforms({
            ...savedPlatforms,
            facebook: {
              pageId: savedPlatforms.facebook?.pageId || '',
              accessToken: savedPlatforms.facebook?.accessToken || ''
            }
          });
        }

        setLoading(false);
      }, 600); // 600ms delay to show skeleton
    }
  }, [selectedClient]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveChanges = async () => {
    if (!selectedClient) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'No client selected'
      });
      return;
    }

    setSaving(true);
    dispatch(showLoading());

    try {
      // Prepare clientFlags array
      let updatedClientFlags = selectedClient.clientFlags || [];

      if (noReviews && !updatedClientFlags.includes('NO_REVIEWS')) {
        updatedClientFlags.push('NO_REVIEWS');
      } else if (!noReviews && updatedClientFlags.includes('NO_REVIEWS')) {
        updatedClientFlags = updatedClientFlags.filter(
          (flag) => flag !== 'NO_REVIEWS'
        );
      }

      const locationId = selectedClient?.id || selectedClient?.currentClientId;

      // Save review settings to the dedicated ReviewSettings table
      await axios.post(`${SEO_AUDIT_API}/reviews/settings`, {
        locationId,
        reviewPlatforms,
        maxAiCharacters,
        brandTone,
        aiContext,
        automaticReviews,
        autoPublishAbove: Number(autoPublishAbove),
        requireApprovalBelow: Number(requireApprovalBelow),
        singlePerson: singlePersonBusiness,
        clientFlags: updatedClientFlags
      });

      setSuccessModalOpen(true);
    } catch (error) {
      console.error('Error saving review settings:', error);
      setErrorModalOpen(true);
    } finally {
      setSaving(false);
      dispatch(hideLoading());
    }
  };

  return (
    <div className="flex min-h-screen bg-muted/30">
      {/* Main Content */}
      <div className="flex-1">
        <main className="p-6 lg:p-10 space-y-8">
          {/* Page Title */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-2">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="icon"
                className="h-12 w-12 rounded-xl"
                onClick={() => history.push('/reviews-v2')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
                  Reviews Settings
                </h1>
                <p className="text-base text-muted-foreground mt-1">
                  Control review management features for this account
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button
                size="lg"
                onClick={handleSaveChanges}
                disabled={saving}
                style={{ paddingLeft: '2rem', paddingRight: '2rem' }}
                className="bg-primary hover:bg-primary/90 text-primary-foreground h-14 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all gap-3 flex items-center justify-center">
                <Save className="h-5 w-5" />
                {saving && <Loader2 className="h-5 w-5 animate-spin" />}
                {!saving && 'Save Changes'}
              </Button>
            </div>
          </div>

          {/* Reviews Settings Card */}
          {loading ? (
            <Card className="shadow-md rounded-2xl border-0">
              <CardHeader className="pb-4 pt-6 px-6 lg:px-8">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-gray-200 animate-pulse" />
                  <div className="flex-1 space-y-2">
                    <div className="h-6 w-48 bg-gray-200 rounded animate-pulse" />
                    <div className="h-4 w-64 bg-gray-200 rounded animate-pulse" />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6 px-6 lg:px-8 pb-8">
                {/* Skeleton Settings Items */}
                {Array.from({ length: 3 }).map((_, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center justify-between py-4 ${
                      idx < 2 ? 'border-b border-border' : ''
                    }`}>
                    <div className="space-y-2 flex-1">
                      <div className="h-5 w-40 bg-gray-200 rounded animate-pulse" />
                      <div className="h-4 w-56 bg-gray-200 rounded animate-pulse" />
                    </div>
                    <div className="h-6 w-11 bg-gray-200 rounded-full animate-pulse" />
                  </div>
                ))}
              </CardContent>
            </Card>
          ) : (
            <Card className="shadow-md rounded-2xl border-0">
              <CardHeader className="pb-4 pt-6 px-6 lg:px-8">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <ToggleLeft className="h-5 w-5 scale-x-125 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-xl lg:text-2xl font-bold">
                      Reviews Settings
                    </CardTitle>
                    <CardDescription className="text-base mt-1">
                      Control review management features for this account
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6 px-6 lg:px-8 pb-8">
                {/* Automatic Reviews */}
                <div
                  className={`flex items-center justify-between py-4 ${
                    !automaticReviews ? 'border-b border-border' : ''
                  }`}>
                  <div className="space-y-1">
                    <Label className="text-base font-semibold">
                      Automatic Reviews
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Automatically respond to customer reviews
                    </p>
                  </div>
                  <Switch
                    checked={automaticReviews}
                    onCheckedChange={setAutomaticReviews}
                    className="data-[state=checked]:bg-primary scale-75"
                  />
                </div>

                {automaticReviews && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6 pt-2 border-b border-border">
                    <div className="space-y-3 p-4 rounded-xl bg-muted/30 border border-border">
                      <div>
                        <Label className="text-sm font-semibold">
                          Auto-publish above X stars
                        </Label>
                        <p className="text-xs text-muted-foreground mt-1">
                          Reviews with this rating or higher will be
                          automatically responded to and published.
                        </p>
                      </div>
                      <select
                        value={autoPublishAbove}
                        onChange={(e) => setAutoPublishAbove(e.target.value)}
                        className="w-full h-11 rounded-xl border-2 border-input bg-background px-3 py-2 text-sm focus:border-primary outline-none">
                        <option value="5">5 Stars</option>
                        <option value="4">4 Stars</option>
                        <option value="3">3 Stars</option>
                        <option value="2">2 Stars</option>
                        <option value="1">1 Star</option>
                      </select>
                    </div>

                    <div className="space-y-3 p-4 rounded-xl bg-muted/30 border border-border">
                      <div>
                        <Label className="text-sm font-semibold">
                          Require approval below Y stars
                        </Label>
                        <p className="text-xs text-muted-foreground mt-1">
                          Reviews with this rating or lower will only generate a
                          draft and require manual approval.
                        </p>
                      </div>
                      <select
                        value={requireApprovalBelow}
                        onChange={(e) =>
                          setRequireApprovalBelow(e.target.value)
                        }
                        className="w-full h-11 rounded-xl border-2 border-input bg-background px-3 py-2 text-sm focus:border-primary outline-none">
                        <option value="5">5 Stars</option>
                        <option value="4">4 Stars</option>
                        <option value="3">3 Stars</option>
                        <option value="2">2 Stars</option>
                        <option value="1">1 Star</option>
                      </select>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* AI Response Settings Card */}
          {!loading && (
            <>
              <Card className="shadow-md rounded-2xl border-0 mt-8">
                <CardHeader className="pb-4 pt-6 px-6 lg:px-8">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Bot className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-xl lg:text-2xl font-bold">
                        AI Response Settings
                      </CardTitle>
                      <CardDescription className="text-base mt-1">
                        Configure how the AI drafts responses to your reviews
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6 px-6 lg:px-8 pb-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Brand Tone */}
                    <div className="space-y-3">
                      <Label
                        htmlFor="brandTone"
                        className="text-base font-semibold">
                        Brand Voice/Tone
                      </Label>
                      <Select value={brandTone} onValueChange={setBrandTone}>
                        <SelectTrigger className="h-14 text-lg rounded-xl border-2 focus:ring-primary px-4">
                          <SelectValue placeholder="Select tone" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="professional">
                            Professional
                          </SelectItem>
                          <SelectItem value="friendly">Friendly</SelectItem>
                          <SelectItem value="casual">Casual</SelectItem>
                          <SelectItem value="formal">Formal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Max Characters (Global -> Platform Specific) */}
                    <div className="space-y-4 md:col-span-2 mt-4 pt-4 border-t border-border">
                      <div>
                        <Label className="text-base font-semibold">
                          Maximum Output Characters (Platform Specific)
                        </Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          Define how long the AI response should be for each
                          platform.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {Object.keys(maxAiCharacters).map((platform) => {
                          const info = PLATFORM_LIMIT_INFO[platform];
                          return (
                            <div
                              key={platform}
                              className="space-y-3 border border-border rounded-xl bg-background p-4 flex flex-col justify-between h-auto min-h-[160px]">
                              <div className="space-y-2">
                                <Label
                                  htmlFor={`max-${platform}`}
                                  className="text-sm font-semibold capitalize">
                                  {platform === 'bbb' ? 'BBB' : platform} Limit
                                </Label>
                                <Input
                                  id={`max-${platform}`}
                                  type="number"
                                  value={maxAiCharacters[platform]}
                                  onChange={(e) =>
                                    setMaxAiCharacters((prev) => ({
                                      ...prev,
                                      [platform]: e.target.value
                                    }))
                                  }
                                  placeholder="e.g. 1000"
                                  className="h-11 rounded-xl border-2 focus:border-primary px-3"
                                />
                              </div>
                              {info && (
                                <div className="mt-4 pt-3 border-t border-border/50 text-xs text-muted-foreground space-y-1.5 flex-1 flex flex-col justify-end">
                                  <p className="font-medium text-foreground">
                                    <span className="text-muted-foreground font-normal">
                                      Limit:
                                    </span>{' '}
                                    {info.limit}
                                  </p>
                                  <p className="leading-snug">{info.details}</p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Additional Context */}
                  <div className="space-y-3 mt-6">
                    <Label
                      htmlFor="aiContext"
                      className="text-base font-semibold">
                      Additional Context / Instructions
                    </Label>
                    <p className="text-sm text-muted-foreground -mt-1">
                      Provide any specific rules, names, or constraints the AI
                      should consider when drafting.
                    </p>
                    <Textarea
                      id="aiContext"
                      value={aiContext}
                      onChange={(e) => setAiContext(e.target.value)}
                      rows={4}
                      placeholder="E.g., Always mention our 100% satisfaction guarantee. Do not use exclamation marks."
                      className="min-h-[120px] text-lg rounded-xl border-2 focus:border-primary p-4 resize-y"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Danger Zone Card */}
              <Card className="shadow-md rounded-2xl border-2 border-red-200 mt-8">
                <CardHeader className="pb-4 pt-6 px-6 lg:px-8">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center">
                      <AlertTriangle className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <CardTitle className="text-xl lg:text-2xl font-bold text-red-700">
                        Danger Zone
                      </CardTitle>
                      <CardDescription className="text-base mt-1">
                        Irreversible actions — proceed with caution
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="px-6 lg:px-8 pb-8">
                  <div className="flex items-center justify-between p-4 rounded-xl border border-red-200 bg-red-50/50">
                    <div className="space-y-1">
                      <Label className="text-base font-semibold text-red-700">
                        Delete All Reviews
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Permanently delete all reviews from all platforms for
                        this client. This cannot be undone.
                      </p>
                    </div>
                    <Button
                      variant="destructive"
                      size="lg"
                      disabled={deletingAll}
                      className="gap-2 rounded-xl font-semibold"
                      onClick={async () => {
                        const locationId =
                          selectedClient?.id || selectedClient?.currentClientId;
                        if (!locationId) return;

                        const result = await Swal.fire({
                          title: 'Delete All Reviews?',
                          text: 'This will permanently delete ALL reviews from ALL platforms for this client. This action cannot be undone.',
                          icon: 'warning',
                          showCancelButton: true,
                          confirmButtonColor: '#dc2626',
                          cancelButtonColor: '#6b7280',
                          confirmButtonText: 'Yes, delete everything',
                          cancelButtonText: 'Cancel'
                        });

                        if (!result.isConfirmed) return;

                        // Second confirmation
                        const confirm2 = await Swal.fire({
                          title: 'Are you absolutely sure?',
                          text: `Type "DELETE" to confirm deletion of all reviews for this client.`,
                          icon: 'warning',
                          input: 'text',
                          inputPlaceholder: 'Type DELETE',
                          showCancelButton: true,
                          confirmButtonColor: '#dc2626',
                          confirmButtonText: 'Delete All',
                          inputValidator: (value) => {
                            if (value !== 'DELETE')
                              return 'Please type DELETE to confirm';
                          }
                        });

                        if (!confirm2.isConfirmed) return;

                        setDeletingAll(true);
                        try {
                          const res = await axios.delete(
                            `${SEO_AUDIT_API}/reviews/all`,
                            {
                              params: {
                                locationId: String(locationId),
                                email: userInfo?.email
                              }
                            }
                          );
                          const { deleted, errors } = res.data;
                          await Swal.fire({
                            title: 'Deleted!',
                            text: `Successfully deleted ${deleted} reviews${
                              errors > 0 ? ` (${errors} errors)` : ''
                            }.`,
                            icon: 'success',
                            confirmButtonColor: '#3766a8'
                          });

                          window.dispatchEvent(
                            new Event('reviews-v2-refresh-requested')
                          );
                        } catch (err) {
                          console.error('Error deleting all reviews:', err);
                          Swal.fire({
                            title: 'Error!',
                            text: 'Failed to delete reviews. Please try again.',
                            icon: 'error'
                          });
                        } finally {
                          setDeletingAll(false);
                        }
                      }}>
                      {deletingAll ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                      {deletingAll ? 'Deleting...' : 'Delete All Reviews'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </main>
      </div>

      {/* Success Modal */}
      <Dialog open={successModalOpen} onOpenChange={setSuccessModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              Success!
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              Review settings updated successfully.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={() => setSuccessModalOpen(false)}>
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error Modal */}
      <Dialog open={errorModalOpen} onOpenChange={setErrorModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              Error!
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              Failed to update review settings.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={() => setErrorModalOpen(false)}>
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReviewsSettings;
